import React, { useState } from "react";
import { POLICY_TEXT_LIMITS, tooLongMessage, apiErrorMessage } from "../utils/textLimits";

const STATUS_CLS = {
    Requested: "bg-blue-50 text-blue-700 border-blue-200",
    Active: "bg-amber-50 text-amber-700 border-amber-200",
    Scheduled: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Rejected: "bg-gray-100 text-gray-500 border-gray-200",
    Revoked: "bg-gray-100 text-gray-500 border-gray-200",
    Expired: "bg-red-50 text-red-700 border-red-200",
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// Org-wide policy exception register (prototype "Exceptions" view): policy / requirement, affected
// object, risk owner, expiry, decision. The assigned approver (or an org admin) decides inline.
const PolicyExceptionsTab = ({ items, apiCall, canManage, canAdmin, currentUserId, onChanged, onOpenPolicy }) => {
    const [comment, setComment] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const act = async (row, action) => {
        const c = comment[row.exception.id] || "";
        if ((action === "approve" || action === "reject") && !c.trim()) { setError("Enter a decision reason."); return; }
        const tooLong = tooLongMessage("Decision reason", c, POLICY_TEXT_LIMITS.exceptionDecisionReason);
        if (tooLong) { setError(tooLong); return; }
        setBusy(true); setError(null);
        try { await apiCall("PUT", `/security-insights/policy-docs/${row.policyId}/exceptions/${row.exception.id}`, { action, comment: c }); onChanged?.(); }
        catch (e) { setError(apiErrorMessage(e, "Exception action failed.")); }
        finally { setBusy(false); }
    };

    if (!items) return <p className="text-xs text-gray-400">Loading exceptions…</p>;
    if (items.length === 0) return <p className="text-xs text-gray-400">No exceptions recorded. Open a policy to request one against a published requirement.</p>;

    return (
        <div className="space-y-2">
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead><tr className="text-left text-gray-400 uppercase tracking-wide border-b border-gray-200">
                            <th className="px-3 py-2">Policy / requirement</th><th className="px-3 py-2">Affected object</th><th className="px-3 py-2">Risk owner</th><th className="px-3 py-2">Approver</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Decision</th><th className="px-3 py-2"></th>
                        </tr></thead>
                        <tbody>
                            {items.map(row => {
                                const e = row.exception;
                                const canDecide = e.status === "Requested" && ((e.approverUserId && e.approverUserId === currentUserId) || canAdmin || (!e.approverUserId && canManage));
                                return (
                                    <tr key={e.id} className="border-b border-gray-100 align-top">
                                        <td className="px-3 py-2">
                                            <button onClick={() => onOpenPolicy?.(row.policyId, "exceptions")} className="text-indigo-700 font-medium hover:underline text-left">{row.policyTitle}</button>
                                            <div className="text-gray-800">{e.title}</div>
                                            <div className="text-gray-400">v{e.policyVersion ?? "—"}{e.requirementText ? ` · ${e.requirementText}` : ""}{e.requirementInCurrentVersion === false ? " (requirement no longer in the current version)" : ""}</div>
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">{e.affectedObject || "—"}</td>
                                        <td className="px-3 py-2 text-gray-600">{e.riskOwnerName || "—"}</td>
                                        <td className="px-3 py-2 text-gray-600">{e.approverName || (e.approverUserId ? `user ${e.approverUserId}` : "any manager")}</td>
                                        <td className="px-3 py-2 text-gray-600">{fmt(e.expiryDate)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_CLS[e.displayStatus] || STATUS_CLS.Rejected}`}>{e.displayStatus}</span>
                                            {e.decisionComment && <div className="text-gray-400 mt-0.5">{e.decisionComment}</div>}
                                            {(e.decidedByOverride || e.revokedByOverride) && <div className="text-amber-700 mt-0.5">decided by org admin override</div>}
                                        </td>
                                        <td className="px-3 py-2">
                                            {canDecide && (
                                                <div className="flex items-center gap-1">
                                                    <input value={comment[e.id] || ""} onChange={ev => setComment(c => ({ ...c, [e.id]: ev.target.value }))} placeholder="Reason" title={`Decision reason, up to ${POLICY_TEXT_LIMITS.exceptionDecisionReason.toLocaleString("en-US")} characters`} className="border border-gray-200 rounded px-1.5 py-1 w-28" />
                                                    <button disabled={busy} onClick={() => act(row, "approve")} className="text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">Approve</button>
                                                    <button disabled={busy} onClick={() => act(row, "reject")} className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">Reject</button>
                                                </div>
                                            )}
                                            {canManage && e.status === "Active" && <button disabled={busy} onClick={() => act(row, "revoke")} className="text-gray-600 bg-gray-100 border border-gray-300 rounded px-2 py-1">Revoke</button>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PolicyExceptionsTab;
